import { derinApiClient } from "./setup";
import {
  AdminSurveyRequestSummary,
  DeleteSurveyRequestResult,
  DerinApiResponse,
  SurveyRequestMarkdown,
} from "./interface";

const base = (organizationId: string) =>
  `/survey-request/organization/${organizationId}`;

/** Submitted survey requests of one organization, newest first. Drafts are never listed. */
export const getSurveyRequestsByOrganization = async (
  organizationId: string
): Promise<AdminSurveyRequestSummary[]> => {
  const response = await derinApiClient().get<
    DerinApiResponse<AdminSurveyRequestSummary[]>
  >(base(organizationId));
  return response.data.data;
};

/**
 * The brief rendered as Markdown. Comes back as JSON, like the collection config, and
 * the caller builds the file — so errors keep the API's `{ message }` envelope.
 */
export const getSurveyRequestMarkdown = async (
  organizationId: string,
  surveyRequestId: string
): Promise<SurveyRequestMarkdown> => {
  const response = await derinApiClient().get<
    DerinApiResponse<SurveyRequestMarkdown>
  >(`${base(organizationId)}/${surveyRequestId}/markdown`);
  return response.data.data;
};

/** Soft delete: the request disappears from the portal and from the client's My Surveys. */
export const deleteSurveyRequest = async (
  organizationId: string,
  surveyRequestId: string
): Promise<DeleteSurveyRequestResult> => {
  const response = await derinApiClient().delete<
    DerinApiResponse<DeleteSurveyRequestResult>
  >(`${base(organizationId)}/${surveyRequestId}`);
  return response.data.data;
};

/**
 * Raw bytes of an attached file (no envelope). On failure axios still hands back a Blob,
 * so the API's JSON error body has to be read from it — see `blobErrorMessage`.
 */
export const downloadSurveyRequestFile = async (
  organizationId: string,
  surveyRequestId: string,
  fileId: string
): Promise<Blob> => {
  const response = await derinApiClient().get<Blob>(
    `${base(organizationId)}/${surveyRequestId}/files/${fileId}/download`,
    { responseType: "blob" }
  );
  return response.data;
};
